/* eslint-disable array-callback-return */
import {
  TooltipHost,
  TooltipDelay,
  DirectionalHint,
  ITooltipProps,
  Icon,
  mergeStyleSets,
} from "@fluentui/react";
import { Text } from "@fluentui/react";
import { complianceResult } from "../objects/complianceResult";
import { renderComplianceTooltip } from "../utils";
import { useId } from "@fluentui/react-hooks";
import { ComplianceRule } from "../enums";

interface complianceprops {
  complianceResults: Array<complianceResult>;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = mergeStyleSets({
  // Chip base
  chipBase: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 8px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "default",
    userSelect: "none" as const,
  },
  chipCompliant: {
    background: "#dff6dd",
    color: "#107c10",
    border: "1px solid #bad80a",
  },
  chipNonCompliant: {
    background: "#fde7e9",
    color: "#a4262c",
    border: "1px solid #f1707b",
  },
  chipExpired: {
    background: "#fff4ce",
    color: "#7a4f00",
    border: "1px solid #f7c948",
  },
});

// ── Variant map ───────────────────────────────────────────────────────────────
type StatusVariant = "compliant" | "nonCompliant" | "expired";

const variantConfig: Record<
  StatusVariant,
  { iconName: string; chipClass: string }
> = {
  compliant: {
    iconName: "CheckMark",
    chipClass: `${styles.chipBase} ${styles.chipCompliant}`,
  },
  nonCompliant: {
    iconName: "ErrorBadge",
    chipClass: `${styles.chipBase} ${styles.chipNonCompliant}`,
  },
  expired: {
    iconName: "Warning",
    chipClass: `${styles.chipBase} ${styles.chipExpired}`,
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
export function ComplianceField(props: complianceprops) {
  const tooltipId = useId("tooltip");

  // ── Derive state ────────────────────────────────────────────────────────────
  const isCompliant = props.complianceResults.every(
    (compResult) => compResult.isCompliant === true,
  );
  const isExpired = props.complianceResults.some(
    (compResult) => compResult.isExpired === true,
  );

  let label = "Compliant";
  let variant: StatusVariant = "compliant";

  if (!isCompliant) {
    const nonCompliantRule = props.complianceResults.find(
      (result) =>
        result.isCompliant === false &&
        result.complianceRule !== ComplianceRule.LeaseExpiration,
    );
    label = nonCompliantRule?.complianceLabel ?? "Non-Compliant";
    variant = "nonCompliant";
  }

  if (isExpired) {
    label = "Expired";
    variant = "expired";
  }

  // ── Tooltip content ─────────────────────────────────────────────────────────
  const tooltipProps: ITooltipProps = {
    onRenderContent: () => (
      <div style={{ padding: "12px 8px", maxWidth: "280px" }}>
        {renderComplianceTooltip(isExpired, isCompliant)}
      </div>
    ),
  };

  const { iconName, chipClass } = variantConfig[variant];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <TooltipHost
      tooltipProps={tooltipProps}
      delay={TooltipDelay.zero}
      id={tooltipId}
      directionalHint={DirectionalHint.bottomCenter}
      calloutProps={{
        gapSpace: 4,
        styles: {
          beak: { background: "#ffffff" },
          beakCurtain: { background: "#ffffff" },
          calloutMain: {
            background: "#ffffff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.14)",
            borderRadius: "6px",
            border: "1px solid #edebe9",
          },
        },
      }}
      styles={{ root: { display: "inline-block" } }}
    >
      <span className={chipClass}>
        <Icon iconName={iconName} styles={{ root: { fontSize: 11 } }} />
        <Text
          styles={{
            root: {
              fontSize: "12px",
              fontWeight: 600,
              color: "inherit",
            },
          }}
        >
          {label}
        </Text>
      </span>
    </TooltipHost>
  );
}